import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  usePackageDetail,
  useYankPackage,
  useYankVersion,
  type VersionRow,
} from "../hooks/usePackages.js";
import { useSkillMd } from "../hooks/useSkillMd.js";
import { formatDate, formatRelative } from "../lib/format.js";
import { avatarColor, avatarInitial } from "../lib/avatar-color.js";
import { EditSkillModal } from "../components/EditSkillModal.js";

type Tab = "overview" | "install" | "versions";

export function PackageDetailPage() {
  const { name } = useParams<{ name: string }>();
  const { data, isLoading, error } = usePackageDetail(name);
  const yankPkg = useYankPackage();
  const yankVer = useYankVersion();
  const [tab, setTab] = useState<Tab>("overview");
  const [editing, setEditing] = useState(false);

  const latest = data?.versions[0];
  const skillMd = useSkillMd(name, latest?.version);

  if (isLoading) return <div className="empty">加载中…</div>;
  if (error) return <div className="err">{(error as Error).message}</div>;
  if (!data) return null;

  const { package: pkg, versions } = data;
  const files = latest ? (JSON.parse(latest.files_json) as FileInfo[]) : [];
  const tags = latest?.tags_json ? (JSON.parse(latest.tags_json) as string[]) : [];

  const channelLabel: Record<string, string> = {
    official: "官方",
    community: "社区",
    private: "企业私有",
  };

  return (
    <div style={{ maxWidth: 960, margin: "0 auto" }}>
      {/* 面包屑 */}
      <div
        style={{
          fontSize: 12,
          color: "var(--text-muted)",
          marginBottom: 20,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <Link to="/packages">← 包管理</Link>
        <span>/</span>
        <code style={{ color: "var(--text)" }}>{pkg.name}</code>
      </div>

      {/* 头部:大头像 + 标题 + 描述 + 操作 */}
      <div style={{ display: "flex", gap: 20, alignItems: "flex-start", marginBottom: 28 }}>
        <div
          className="avatar avatar-lg"
          style={{ background: avatarColor(pkg.name) }}
        >
          {avatarInitial(pkg.name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <h1 style={{ fontSize: 24, margin: 0, fontWeight: 600, letterSpacing: "-0.02em" }}>
              {latest?.display_name || pkg.name}
            </h1>
            {pkg.yanked === 1 ? (
              <span className="pill pill-danger">已下架</span>
            ) : (
              <span className="pill pill-success">上架中</span>
            )}
          </div>
          <div
            className="mono"
            style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}
          >
            {pkg.name}
          </div>
          {latest?.description && (
            <p
              style={{
                fontSize: 14,
                color: "var(--text)",
                marginTop: 12,
                lineHeight: 1.6,
              }}
            >
              {latest.description}
            </p>
          )}
          {tags.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
              {tags.map((t) => (
                <span key={t} className="pill pill-neutral">
                  #{t}
                </span>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {latest && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setEditing(true)}
              title="改 displayName / 描述 / tags / changelog / channel"
            >
              ✎ 编辑元数据
            </button>
          )}
          <button
            className={pkg.yanked ? "btn btn-primary btn-sm" : "btn btn-danger btn-sm"}
            onClick={() => yankPkg.mutate({ name: pkg.name, yank: pkg.yanked === 0 })}
            disabled={yankPkg.isPending}
          >
            {pkg.yanked ? "恢复上架" : "下架整个包"}
          </button>
        </div>
      </div>

      {editing && latest && (
        <EditSkillModal pkg={pkg} latest={latest} onClose={() => setEditing(false)} />
      )}

      {/* 指标卡条 */}
      <div
        className="card"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          overflow: "hidden",
          marginBottom: 24,
        }}
      >
        <MetricCell label="频道" value={channelLabel[pkg.channel] ?? pkg.channel} />
        <MetricCell
          label="版本"
          value={<span className="mono">v{pkg.latest_version ?? "—"}</span>}
        />
        <MetricCell label="类型" value={pkg.family} />
        <MetricCell label="下载量" value={pkg.download_count.toLocaleString()} />
        <MetricCell
          label="收藏"
          value={pkg.star_count.toLocaleString()}
          last
        />
      </div>

      {/* 安全检测 section */}
      <div
        className="card"
        style={{
          background: "var(--success-soft)",
          borderColor: "#a7f3d0",
          padding: 16,
          marginBottom: 24,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 16 }}>🛡️</span>
          <div style={{ fontSize: 13, fontWeight: 600 }}>安全检测</div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 10,
          }}
        >
          <div
            className="card"
            style={{
              padding: "10px 14px",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 500 }}>mhwork 实验室</span>
            <span className="pill pill-success">● 安全,无风险</span>
          </div>
          <div
            className="card"
            style={{
              padding: "10px 14px",
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "var(--text-muted)",
              fontStyle: "italic",
            }}
          >
            <span style={{ fontSize: 12 }}>外部扫描:待接入第三方</span>
          </div>
        </div>
        <p
          style={{
            fontSize: 11,
            color: "var(--text-muted)",
            marginTop: 10,
            marginBottom: 0,
            lineHeight: 1.6,
          }}
        >
          安全检测结果由 mhwork 内部流程出具,仅作参考。网络安全具有动态性,
          请自行做好安全防护和风险管控。
        </p>
      </div>

      {/* Tab 切换 */}
      <div
        style={{
          display: "flex",
          gap: 4,
          borderBottom: "1px solid var(--border)",
          marginBottom: 20,
        }}
      >
        <TabButton active={tab === "overview"} onClick={() => setTab("overview")}>
          概述
        </TabButton>
        <TabButton active={tab === "install"} onClick={() => setTab("install")}>
          安装方式
        </TabButton>
        <TabButton active={tab === "versions"} onClick={() => setTab("versions")}>
          版本历史
        </TabButton>
      </div>

      {tab === "overview" && (
        <OverviewTab
          skillMd={skillMd.data}
          loading={skillMd.isLoading}
          fallback={latest?.description ?? undefined}
          changelog={latest?.changelog}
          version={latest?.version}
        />
      )}

      {tab === "install" && (
        <InstallTab
          name={pkg.name}
          version={pkg.latest_version ?? ""}
          files={files}
        />
      )}

      {tab === "versions" && (
        <VersionsTab
          versions={versions}
          onYank={(version) => yankVer.mutate({ name: pkg.name, version })}
          yanking={yankVer.isPending}
        />
      )}

      {/* 底部元数据 */}
      <div
        style={{
          marginTop: 28,
          paddingTop: 16,
          borderTop: "1px solid var(--border-soft)",
          display: "flex",
          gap: 24,
          fontSize: 11,
          color: "var(--text-muted)",
        }}
      >
        <span>创建 {formatDate(pkg.created_at)}</span>
        <span>·</span>
        <span>最后更新 {formatRelative(pkg.updated_at)}</span>
        {latest && (
          <>
            <span>·</span>
            <span>
              发布者 <code style={{ fontSize: 10 }}>{latest.published_by.slice(0, 8)}</code>
            </span>
          </>
        )}
      </div>
    </div>
  );
}

interface FileInfo {
  path: string;
  size: number;
  storageId: string;
  sha256: string;
}

function MetricCell({
  label,
  value,
  last,
}: {
  label: string;
  value: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div
      style={{
        padding: "14px 16px",
        borderRight: last ? 0 : "1px solid var(--border-soft)",
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: "var(--text-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "10px 16px",
        background: "none",
        border: 0,
        borderBottom: active ? "2px solid var(--primary)" : "2px solid transparent",
        color: active ? "var(--text)" : "var(--text-muted)",
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        cursor: "pointer",
        marginBottom: -1,
      }}
    >
      {children}
    </button>
  );
}

function OverviewTab({
  skillMd,
  loading,
  fallback,
  changelog,
  version,
}: {
  skillMd: string | null | undefined;
  loading: boolean;
  fallback?: string;
  changelog?: string;
  version?: string;
}) {
  if (loading) return <div className="empty">加载 SKILL.md…</div>;

  if (!skillMd) {
    return (
      <div className="card" style={{ padding: 20 }}>
        {fallback ? (
          <p style={{ fontSize: 14, lineHeight: 1.6, margin: 0 }}>{fallback}</p>
        ) : (
          <div className="empty">该版本未提供 SKILL.md</div>
        )}
        {changelog && (
          <div style={{ marginTop: 20, paddingTop: 20, borderTop: "1px solid var(--border-soft)" }}>
            <h3 style={{ fontSize: 13, fontWeight: 600, margin: "0 0 8px" }}>
              Changelog {version && <span className="code-soft">v{version}</span>}
            </h3>
            <pre
              style={{
                margin: 0,
                fontFamily: "var(--font-sans)",
                fontSize: 13,
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
                color: "var(--text-muted)",
              }}
            >
              {changelog}
            </pre>
          </div>
        )}
      </div>
    );
  }

  // YAML frontmatter(--- ... ---)跟上方基础信息重复,去掉只渲染正文
  const body = stripFrontmatter(skillMd);

  return (
    <div className="card skill-md-content" style={{ padding: "28px 36px" }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
    </div>
  );
}

function stripFrontmatter(md: string): string {
  // 只有以 "---\n" 开头才视为 frontmatter;结束符也是独立一行的 ---
  if (!/^---\s*\n/.test(md)) return md;
  const match = md.match(/^---\s*\n[\s\S]*?\n---\s*(?:\n|$)/);
  return match ? md.slice(match[0].length) : md;
}

function InstallTab({
  name,
  version,
  files,
}: {
  name: string;
  version: string;
  files: FileInfo[];
}) {
  const archive =
    files.find((f) => f.path.toLowerCase().endsWith(".zip")) ??
    files.find((f) => /\.(tar\.gz|tgz)$/i.test(f.path)) ??
    files[0];
  return (
    <div className="card" style={{ padding: 20 }}>
      <h3 style={{ fontSize: 13, fontWeight: 600, margin: "0 0 12px" }}>
        从 mhwork 客户端安装
      </h3>
      <p style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6, margin: 0 }}>
        在 mhwork 桌面端"技能市场"里搜索{" "}
        <code className="code-soft">{name}</code>,点击安装。技能会下载到{" "}
        <code className="code-soft">~/.mhwork/skills/hub/{name}@{version}/</code>。
      </p>

      <h3 style={{ fontSize: 13, fontWeight: 600, margin: "24px 0 10px" }}>
        CLI 方式(兼容 ClawHub)
      </h3>
      <pre
        style={{
          background: "var(--surface-soft)",
          borderRadius: "var(--radius)",
          padding: "12px 14px",
          margin: 0,
          fontSize: 12,
          fontFamily: "var(--font-mono)",
          overflowX: "auto",
        }}
      >
        CLAWHUB_REGISTRY=https://skills.metrichub.app clawhub install {name}@{version}
      </pre>

      <h3 style={{ fontSize: 13, fontWeight: 600, margin: "24px 0 10px" }}>包文件</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {files.map((f) => (
          <div
            key={f.storageId}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 12px",
              background: "var(--surface-soft)",
              borderRadius: "var(--radius)",
              fontSize: 12,
            }}
          >
            <span>{f === archive ? "📦" : "📄"}</span>
            <span
              className="mono"
              style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {f.path}
            </span>
            <span style={{ color: "var(--text-muted)", fontSize: 11 }}>
              {formatSize(f.size)}
            </span>
            <span
              className="mono"
              style={{ color: "var(--text-subtle)", fontSize: 10 }}
              title={f.sha256}
            >
              {f.sha256.slice(0, 10)}…
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function VersionsTab({
  versions,
  onYank,
  yanking,
}: {
  versions: VersionRow[];
  onYank: (version: string) => void;
  yanking: boolean;
}) {
  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {versions.map((v) => (
          <div
            key={v.version}
            style={{
              padding: 14,
              background: "var(--surface-soft)",
              borderRadius: "var(--radius)",
              opacity: v.yanked ? 0.5 : 1,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="mono" style={{ fontSize: 14, fontWeight: 600 }}>
                v{v.version}
              </span>
              {v.yanked === 1 && <span className="pill pill-danger">已下架</span>}
              <span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: 6 }}>
                {formatRelative(v.published_at)}
              </span>
              <button
                className="btn btn-ghost btn-xs"
                style={{ marginLeft: "auto" }}
                disabled={yanking || v.yanked === 1}
                onClick={() => onYank(v.version)}
              >
                {v.yanked === 1 ? "已下架" : "下架此版本"}
              </button>
            </div>
            {v.changelog && (
              <div
                style={{
                  fontSize: 12,
                  color: "var(--text-muted)",
                  marginTop: 8,
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                }}
              >
                {v.changelog}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

